import { useState } from "react";
import { http } from "@/lib/api";
import { ImagePlus, Video, X } from "lucide-react";
import { toast } from "sonner";

export const mediaSrc = (url) => (url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export function ProductMediaUploader({ images, videoUrl, onChange }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/products/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind === "video") onChange({ images, videoUrl: data.url });
      else onChange({ images: [...images, data.url].slice(0, 3), videoUrl });
      toast.success(`${data.kind === "video" ? "Video" : "Photo"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeImage = (i) => onChange({ images: images.filter((_, idx) => idx !== i), videoUrl });

  return (
    <div className="space-y-3" data-testid="product-media-uploader">
      <div>
        <div className="text-xs font-semibold text-[#1E3A2B] mb-1.5">Product photos ({images.length}/3)</div>
        <div className="flex gap-2 flex-wrap">
          {images.map((url, i) => (
            <div key={url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E2DDD5]" data-testid={`media-image-${i}`}>
              <img src={mediaSrc(url)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" data-testid={`remove-image-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {images.length < 3 && (
            <label className={`w-20 h-20 rounded-lg border-2 border-dashed border-[#E2DDD5] flex flex-col items-center justify-center text-[10px] text-[#5A6B5D] cursor-pointer hover:border-[#1E3A2B] ${busy ? "opacity-50" : ""}`}>
              <ImagePlus className="w-5 h-5 mb-1" />
              {busy ? "Uploading…" : "Add photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="upload-image-input" />
            </label>
          )}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-[#1E3A2B] mb-1.5">Product video (optional, 1 max, up to 50MB)</div>
        {videoUrl ? (
          <div className="relative w-40 rounded-lg overflow-hidden border border-[#E2DDD5]" data-testid="media-video">
            <video src={mediaSrc(videoUrl)} className="w-full h-24 object-cover" muted />
            <button type="button" onClick={() => onChange({ images, videoUrl: null })} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" data-testid="remove-video">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label className={`inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border-2 border-dashed border-[#E2DDD5] cursor-pointer hover:border-[#1E3A2B] ${busy ? "opacity-50" : ""}`}>
            <Video className="w-4 h-4" /> {busy ? "Uploading…" : "Add video (MP4/WEBM/MOV)"}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="upload-video-input" />
          </label>
        )}
      </div>
    </div>
  );
}
